import { cancelFrame, frame } from 'motion';
import { type Transition, motion, useAnimationControls, useReducedMotion } from 'motion/react';
import React, { ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { Icon } from '@column/column-ui-kit';
import { cn } from '@column/ui';

// Keep in sync with the Tailwind classes below: the marker's center sits 20px into the (h-10) control row, and the
// connector stops 4px short of each 20px marker's visible circle edge (radius 7.5 + 0.75 stroke, plus the line cap).
const LINE_INSET_PX = 8.25 + 1 + 4;
const FRAME_PADDING_PX = 16;
const LINE_GAP_HALF_PX = 13;
const SWAP_DURATION = 0.45;

const SURFACE_CLASS = 'rounded-xl border border-gray-100 bg-[#f9f9fa] dark:border-white/10 dark:bg-gray-950';

const FlowChevron: React.FC<{ offset?: boolean; variant?: 'small' | 'large' }> = ({ offset, variant }) => (
  <svg
    width={20}
    height={20}
    viewBox="0 0 20 20"
    fill="none"
    className="absolute inset-0 block overflow-visible"
    aria-hidden
  >
    <path
      d="M5 7.5L10 12.5L15 7.5"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      vectorEffect="non-scaling-stroke"
      className={cn(
        'origin-center [transform-box:fill-box]',
        variant === 'small' && '[transform:translateY(-4px)_scale(0.4)]',
        variant === 'large' && '[transform:translateY(0)_scale(1)]',
        !variant && 'animate-chevron-flow',
        !variant && offset && '[animation-delay:-0.9s]'
      )}
    />
  </svg>
);

export interface SequenceRow {
  key: string;
  label: string;
  description?: string;
  required?: boolean;
  selected?: boolean;
  node: ReactNode;
}

export interface TransferSequenceProps {
  /** Ordered top-to-bottom: the first row is the money origin, the last the destination. */
  rows: SequenceRow[];
  className?: string;
}

export const TransferSequence: React.FC<TransferSequenceProps> = ({ rows, className }) => {
  const reduceMotion = useReducedMotion();
  const labelControls = useAnimationControls();
  const orderKey = rows.map((row) => row.key).join('>');
  const previousOrderKey = useRef(orderKey);
  const animKey = rows.map((row) => `${row.key}:${row.selected ? 1 : 0}`).join('>');
  const previousAnimKey = useRef(animKey);

  const duration = reduceMotion ? 0 : SWAP_DURATION;

  const listRef = useRef<HTMLDivElement>(null);
  const rowRefs = useRef<(HTMLDivElement | null)[]>([]);
  const controlRefs = useRef<(HTMLDivElement | null)[]>([]);
  const markerRefs = useRef<(HTMLSpanElement | null)[]>([]);
  const frameRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const lineTopRef = useRef<SVGLineElement>(null);
  const lineBottomRef = useRef<SVGLineElement>(null);
  const iconRef = useRef<HTMLDivElement>(null);
  const [ready, setReady] = useState(false);

  // Descriptions change with direction; swap the displayed text at the cross-fade's midpoint so it doesn't flip early.
  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  const [displayedDescriptions, setDisplayedDescriptions] = useState<Record<string, string | undefined>>(() =>
    Object.fromEntries(rows.map((row) => [row.key, row.description]))
  );

  // Geometry is written straight to the DOM (not React state) so the connector never lags a frame behind the markers.
  const measure = useCallback(() => {
    const list = listRef.current;
    if (!list) {
      return;
    }

    // The frame uses resting offsets (which ignore FLIP transforms) so it stays put while the rows swap; the
    // connector below uses live rects so it tracks the markers mid-swap.
    const frameEl = frameRef.current;
    const controls = controlRefs.current.filter((c): c is HTMLDivElement => Boolean(c));
    if (frameEl && controls.length >= 2) {
      const offsetTo = (el: HTMLElement, axis: 'offsetTop' | 'offsetLeft') => {
        let value = 0;
        let node: HTMLElement | null = el;
        while (node && node !== list) {
          value += node[axis];
          node = node.offsetParent as HTMLElement | null;
        }
        return value;
      };
      const tops = controls.map((c) => offsetTo(c, 'offsetTop'));
      const lefts = controls.map((c) => offsetTo(c, 'offsetLeft'));
      const top = Math.min(...tops);
      const left = Math.min(...lefts);
      const bottom = Math.max(...controls.map((c, i) => tops[i] + c.offsetHeight));
      const right = Math.max(...controls.map((c, i) => lefts[i] + c.offsetWidth));
      frameEl.style.top = `${top - FRAME_PADDING_PX}px`;
      frameEl.style.left = `${left}px`;
      frameEl.style.width = `${right - left}px`;
      frameEl.style.height = `${bottom - top + FRAME_PADDING_PX * 2}px`;
    }

    const svg = svgRef.current;
    const lineTop = lineTopRef.current;
    const lineBottom = lineBottomRef.current;
    const markers = markerRefs.current.filter((m): m is HTMLSpanElement => Boolean(m));
    if (!svg || !lineTop || !lineBottom || markers.length < 2) {
      return;
    }
    const listRect = list.getBoundingClientRect();
    const centers = markers.map((marker) => {
      const rect = marker.getBoundingClientRect();
      return { x: rect.left - listRect.left + rect.width / 2, y: rect.top - listRect.top + rect.height / 2 };
    });
    const ys = centers.map((center) => center.y);
    const topCenter = Math.min(...ys);
    const bottomCenter = Math.max(...ys);
    const height = Math.max(0, bottomCenter - topCenter - LINE_INSET_PX * 2);
    svg.style.top = `${topCenter + LINE_INSET_PX}px`;
    svg.style.left = `${centers[0].x}px`;
    svg.style.height = `${height}px`;

    const mid = height / 2;
    const gapHalf = Math.min(LINE_GAP_HALF_PX, Math.max(0, mid - 4));
    lineTop.setAttribute('y2', `${Math.max(0, mid - gapHalf)}`);
    lineBottom.setAttribute('y1', `${mid + gapHalf}`);
    lineBottom.setAttribute('y2', `${height}`);

    if (iconRef.current) {
      iconRef.current.style.top = `${(topCenter + bottomCenter) / 2}px`;
      iconRef.current.style.left = `${centers[0].x}px`;
    }
  }, []);

  // Re-measure every frame until a layout animation settles. postRender runs after motion writes the rows'
  // transforms, so the line reads the markers' true positions; repeated calls just extend the window.
  const loopEndRef = useRef(0);
  const stopRafRef = useRef(0);
  const scheduleMeasure = useCallback(() => {
    if (reduceMotion) {
      measure();
      return;
    }
    loopEndRef.current = performance.now() + SWAP_DURATION * 1000;
    frame.postRender(measure, true);
    if (stopRafRef.current) {
      return;
    }
    const watch = () => {
      if (performance.now() >= loopEndRef.current) {
        cancelFrame(measure);
        stopRafRef.current = 0;
        return;
      }
      stopRafRef.current = requestAnimationFrame(watch);
    };
    stopRafRef.current = requestAnimationFrame(watch);
  }, [measure, reduceMotion]);

  useEffect(() => {
    if (previousOrderKey.current === orderKey) {
      return;
    }
    previousOrderKey.current = orderKey;

    const applyDescriptions = () =>
      setDisplayedDescriptions(Object.fromEntries(rowsRef.current.map((row) => [row.key, row.description])));

    if (reduceMotion) {
      labelControls.set({ opacity: 1 });
      applyDescriptions();
      return;
    }
    labelControls.start({
      opacity: [1, 0, 1],
      transition: { duration, times: [0, 0.5, 1], ease: 'easeInOut' },
    });
    const swap = setTimeout(applyDescriptions, (duration / 2) * 1000);
    return () => clearTimeout(swap);
  }, [orderKey, reduceMotion, duration, labelControls]);

  useLayoutEffect(() => {
    if (previousAnimKey.current === animKey) {
      return;
    }
    previousAnimKey.current = animKey;
    scheduleMeasure();
  }, [animKey, scheduleMeasure]);

  useEffect(
    () => () => {
      cancelFrame(measure);
      cancelAnimationFrame(stopRafRef.current);
    },
    [measure]
  );

  useLayoutEffect(() => {
    if (markerRefs.current.filter(Boolean).length >= 2 && !ready) {
      setReady(true);
    }
    measure();
  });

  useEffect(() => {
    const observer = new ResizeObserver(() => scheduleMeasure());
    if (listRef.current) {
      observer.observe(listRef.current);
    }
    rowRefs.current.forEach((row) => {
      if (row) {
        observer.observe(row);
      }
    });
    return () => observer.disconnect();
  }, [scheduleMeasure]);

  const layoutTransition: Transition = { duration, ease: [0.4, 0, 0.2, 1] };

  return (
    <div className={cn('@container/form-field', className)}>
      <div className="@max-tablet/form-field:rounded-xl @max-tablet/form-field:border @max-tablet/form-field:border-gray-100 @max-tablet/form-field:bg-[#f9f9fa] @max-tablet/form-field:p-4 @tablet/form-field:pt-4 dark:@max-tablet/form-field:border-white/10 dark:@max-tablet/form-field:bg-gray-950">
        <div ref={listRef} className="relative flex flex-col gap-4 @tablet/form-field:gap-6">
          <div
            ref={frameRef}
            aria-hidden
            className={cn('pointer-events-none absolute z-0 hidden @tablet/form-field:block', SURFACE_CLASS)}
          />
          {ready && (
            <>
              <svg
                ref={svgRef}
                aria-hidden
                className="pointer-events-none absolute z-0 block w-0.5 -translate-x-1/2 overflow-visible [&_line]:stroke-(--color-border) [&_line]:[stroke-linecap:round] [&_line]:[stroke-width:2]"
              >
                <line ref={lineTopRef} x1={1} y1={0} x2={1} y2={0} />
                <line ref={lineBottomRef} x1={1} y1={0} x2={1} y2={0} />
              </svg>
              <div
                ref={iconRef}
                aria-hidden
                className="pointer-events-none absolute z-1 size-5 -translate-1/2 text-(--color-border)"
              >
                {reduceMotion ? (
                  <>
                    <FlowChevron variant="small" />
                    <FlowChevron variant="large" />
                  </>
                ) : (
                  <>
                    <FlowChevron />
                    <FlowChevron offset />
                  </>
                )}
              </div>
            </>
          )}
          {rows.map((row, index) => (
            <motion.div
              key={row.key}
              ref={(el) => {
                rowRefs.current[index] = el;
              }}
              layout="position"
              transition={layoutTransition}
              className="relative flex flex-col gap-2 @tablet/form-field:grid @tablet/form-field:grid-cols-[minmax(180px,25%)_1fr] @tablet/form-field:items-stretch @tablet/form-field:gap-x-6 @wide/form-field:grid-cols-[minmax(180px,25%)_auto_25%]"
            >
              <motion.div
                animate={labelControls}
                initial={false}
                className="grid gap-0 pl-8 @tablet/form-field:col-start-1 @tablet/form-field:mt-[11px] @tablet/form-field:gap-1 @tablet/form-field:self-start @tablet/form-field:pl-0"
              >
                <div className="text-sm leading-[18px] font-medium text-foreground">
                  {row.label}
                  {row.required && <span className="ml-0.5 inline-block font-bold text-destructive">*</span>}
                </div>
                {displayedDescriptions[row.key] && (
                  <div className="max-w-full text-xs leading-[18px] text-muted-foreground @tablet/form-field:max-w-[32ch]">
                    {displayedDescriptions[row.key]}
                  </div>
                )}
              </motion.div>
              <div
                ref={(el) => {
                  controlRefs.current[index] = el;
                }}
                className={cn(
                  'flex items-stretch gap-3 @tablet/form-field:col-start-2 @tablet/form-field:min-w-0 @tablet/form-field:self-start @tablet/form-field:px-4',
                  // Reserve the frame's bottom padding so a tall (selected) last control can't push it into the next field.
                  index === rows.length - 1 && '@tablet/form-field:mb-4'
                )}
              >
                <div className="w-5 flex-none @tablet/form-field:relative">
                  <span
                    ref={(el) => {
                      markerRefs.current[index] = el;
                    }}
                    className={cn(
                      'absolute top-[9px] left-2.5 z-1 flex -translate-1/2 transition-colors duration-200 @tablet/form-field:top-[20px] @tablet/form-field:left-1/2',
                      row.selected ? 'text-seafoam-600' : 'text-(--color-border)'
                    )}
                  >
                    {row.selected ? <Icon.CircleCheck /> : <Icon.CircleBlank />}
                  </span>
                </div>
                <div className="min-w-0 flex-1">{row.node}</div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};
